import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onClose", "onConfirm", "initialUserIds"],
  _excluded2 = ["onClose", "appName", "appId", "currentUserId", "onConfirm", "tracker", "initialUserIds", "isUngatedForTeamsSelection"];
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { Wizard, WizardStep } from 'foundations-components/transitional/components/Wizard';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { View } from 'foundations-components/components/View';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { ConfirmModal } from 'foundations-components/transitional/components/ConfirmModal';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { AppProvider, useApp } from '../../contexts/AppContext';
import { TeamsGateProvider } from '../../contexts/TeamsGateContext';
import { DIALOG_WIDTH } from '../../constants/panel';
import { InstallPermissions } from '../containers/InstallPermissions';
import { InstallPermissionsProvider } from '../../contexts/InstallPermissionsContext';
import { DataPermissionsProvider, useDataPermissions } from '../../contexts/DataPermissionsContext';
import { SuspenseLoadingState } from '../SuspenseLoadingState/SuspenseLoadingState';
import { DataPermissions } from '../containers/DataPermissions';
import { UnsavedChangesProvider } from '../../contexts/UnsavedChangesContext';
import { useFetchApprovalPermissions } from '../../hooks/queries/useFetchApprovalPermissions';
import { InstallPermissionAccessTypes } from '../../constants/installPermissions';
import { useApproveAppGovernance } from '../../hooks/mutations/useApproveAppGovernance';
import { useInstallPermissions } from '../../contexts/InstallPermissionsContext';
import { TrackingProvider, useTracking } from '../../contexts/TrackingContext';
import { EVENTS, SCREENS, SUBSCREENS, ACTIONS } from '../../constants/tracking';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function ErrorState() {
  return /*#__PURE__*/_jsx(Alert, {
    type: "danger",
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app-governance-lib.errorBoundary.default.title"
    }),
    margin: "600",
    children: /*#__PURE__*/_jsx(Paragraph, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.modal.error"
      })
    })
  });
}
function InstallPermissionsAlert({
  hasDataPermissions
}) {
  return /*#__PURE__*/_jsx(Alert, {
    type: "info",
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app-governance-lib.approveAppPanel.installPermissionsAlert.title"
    }),
    marginBlockEnd: "600",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: hasDataPermissions ? 'app-governance-lib.approveAppPanel.installPermissionsAlert.message' : 'app-governance-lib.approveAppPanel.installPermissionsAlert.messageWithoutDataPermissions'
    })
  });
}
function DataPermissionsAlert() {
  return /*#__PURE__*/_jsx(Alert, {
    type: "info",
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app-governance-lib.approveAppPanel.dataPermissionsAlert.title"
    }),
    marginBlockEnd: "600",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app-governance-lib.approveAppPanel.dataPermissionsAlert.message"
    })
  });
}
function WizardStepWithPadding({
  name,
  children,
  blockSize
}) {
  return /*#__PURE__*/_jsx(WizardStep, {
    name: name,
    paddingBlockStart: "600 !important",
    paddingBlockEnd: "400 !important",
    children: blockSize ? /*#__PURE__*/_jsx(View, {
      blockSize: blockSize,
      children: children
    }) : children
  });
}
function WizardStepInstallPermissions({
  hasDataPermissions
}) {
  const {
    track
  } = useTracking();
  const {
    appId,
    appName
  } = useApp();
  useEffect(() => {
    track(EVENTS.PAGE_VIEWED, {
      screen: SCREENS.APPROVE_APP_PANEL,
      subscreen: SUBSCREENS.INSTALL_PERMISSIONS,
      appId,
      appName
    });
  }, [track, appId, appName]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(InstallPermissionsAlert, {
      hasDataPermissions: hasDataPermissions
    }), /*#__PURE__*/_jsx(InstallPermissions, {})]
  });
}
function WizardStepDataPermissions() {
  const {
    track
  } = useTracking();
  const {
    requiredAppPermissions,
    conditionallyRequiredAppPermissions,
    optionalAppPermissions
  } = useDataPermissions();
  const {
    appId,
    appName
  } = useApp();
  useEffect(() => {
    track(EVENTS.PAGE_VIEWED, {
      screen: SCREENS.APPROVE_APP_PANEL,
      subscreen: SUBSCREENS.DATA_PERMISSIONS,
      appId,
      appName
    });
  }, [track, appId, appName]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(DataPermissionsAlert, {}), /*#__PURE__*/_jsx(DataPermissions, {
      permissionsNotOnAppConfig: [],
      requiredAppPermissions: requiredAppPermissions,
      conditionallyRequiredAppPermissions: conditionallyRequiredAppPermissions,
      optionalAppPermissions: optionalAppPermissions
    })]
  });
}
function ApproveAppDialogWizard({
  onClose,
  onConfirm,
  blockSize
}) {
  const {
    installPermissionAccessState
  } = useInstallPermissions();
  const {
    approveApp,
    loading,
    error
  } = useApproveAppGovernance();
  const {
    track
  } = useTracking();
  const {
    appId,
    appName
  } = useApp();
  const {
    requiredAppPermissions,
    conditionallyRequiredAppPermissions,
    optionalAppPermissions
  } = useDataPermissions();
  const hasDataPermissions = requiredAppPermissions.length > 0 || conditionallyRequiredAppPermissions.length > 0 || optionalAppPermissions.length > 0;
  const handleConfirm = async () => {
    await approveApp();
    track(EVENTS.PANEL_INTERACTED, {
      screen: SCREENS.APPROVE_APP_PANEL,
      action: ACTIONS.APP_APPROVED,
      actionValue: installPermissionAccessState,
      appId,
      appName
    });
    onConfirm(installPermissionAccessState);
  };
  return /*#__PURE__*/_jsxs(Wizard, {
    stickyHeader: false,
    onConfirm: () => void handleConfirm(),
    onReject: onClose,
    defaultHeading: /*#__PURE__*/_jsxs(Flex, {
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.approveAppPanel.title",
        options: {
          appName
        }
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }),
    cancellable: true,
    defaultNextLabel:
    /*#__PURE__*/
    // By default, the next button is "Next: Review data permissions". However, if we add more steps, we'll need to make the wizard controlled/this dynamic.
    _jsx(FormattedMessage, {
      message: "app-governance-lib.approveAppPanel.nextButtonLabel"
    }),
    defaultDoneLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app-governance-lib.approveAppPanel.doneButtonLabel"
    }),
    defaultDoneButton: /*#__PURE__*/_jsx(LoadingButton, {
      loading: loading,
      failed: !!error
    }),
    children: [/*#__PURE__*/_jsx(WizardStepWithPadding, {
      name: "installPermissions",
      blockSize: blockSize,
      children: /*#__PURE__*/_jsx(WizardStepInstallPermissions, {
        hasDataPermissions: hasDataPermissions
      })
    }), hasDataPermissions && /*#__PURE__*/_jsx(WizardStepWithPadding, {
      name: "dataPermissions",
      blockSize: blockSize,
      children: /*#__PURE__*/_jsx(WizardStepDataPermissions, {})
    })]
  });
}
function ApproveAppDialogContentWithData({
  onClose,
  onConfirm,
  blockSize,
  initialUserIds
}) {
  const {
    data: {
      optionalAppPermissions,
      requiredAppPermissions,
      conditionallyRequiredAppPermissions
    } = {
      optionalAppPermissions: [],
      requiredAppPermissions: [],
      conditionallyRequiredAppPermissions: []
    }
  } = useFetchApprovalPermissions();
  const initialEntityTypeAndIds = useMemo(() => {
    // If there are no initial user IDs, we don't need to set the initial entity type and IDs
    if (!initialUserIds || initialUserIds.length === 0) {
      return null;
    }
    return initialUserIds.map(userId => ({
      entityType: 'USER',
      entityId: userId
    }));
  }, [initialUserIds]);
  return /*#__PURE__*/_jsx(DataPermissionsProvider, {
    optionalAppPermissions: optionalAppPermissions,
    requiredAppPermissions: requiredAppPermissions,
    conditionallyRequiredAppPermissions: conditionallyRequiredAppPermissions,
    children: /*#__PURE__*/_jsx(InstallPermissionsProvider, Object.assign({}, initialEntityTypeAndIds ? {
      initialInstallPermissionAccessState: InstallPermissionAccessTypes.SPECIFIC_USERS_AND_TEAMS,
      entityTypeAndIds: initialEntityTypeAndIds
    } : {}, {
      children: /*#__PURE__*/_jsx(ApproveAppDialogWizard, {
        onClose: onClose,
        onConfirm: onConfirm,
        blockSize: blockSize
      })
    }))
  });
}
function ApproveAppDialogContent({
  onClose,
  onConfirm,
  blockSize,
  initialUserIds
}) {
  return /*#__PURE__*/_jsx(SuspenseLoadingState, {
    errorComponent: ErrorState,
    children: /*#__PURE__*/_jsx(ApproveAppDialogContentWithData, {
      onClose: onClose,
      onConfirm: onConfirm,
      blockSize: blockSize,
      initialUserIds: initialUserIds
    })
  });
}
function ApproveAppDialogInner({
  onClose,
  onConfirm,
  initialUserIds,
  type = 'panel',
  blockSize = '50vh'
}) {
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  const handleRequestClose = useCallback(() => {
    setShowLeaveConfirmation(true);
  }, []);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [type === 'modal' ? /*#__PURE__*/_jsx(Modal, {
      width: DIALOG_WIDTH,
      onBackdropClick: handleRequestClose,
      children: /*#__PURE__*/_jsx(ApproveAppDialogContent, {
        onClose: handleRequestClose,
        onConfirm: onConfirm,
        blockSize: blockSize,
        initialUserIds: initialUserIds
      })
    }) : /*#__PURE__*/_jsx(ModalPanel, {
      width: DIALOG_WIDTH,
      onBackdropClick: handleRequestClose,
      children: /*#__PURE__*/_jsx(ApproveAppDialogContent, {
        onClose: handleRequestClose,
        onConfirm: onConfirm,
        initialUserIds: initialUserIds
      })
    }), showLeaveConfirmation && /*#__PURE__*/_jsx(ConfirmModal, {
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.approveAppPanel.leaveConfirmation.title"
      }),
      description: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.approveAppPanel.leaveConfirmation.description"
      }),
      confirmLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.approveAppPanel.leaveConfirmation.confirm"
      }),
      rejectLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.approveAppPanel.leaveConfirmation.reject"
      }),
      onConfirm: onClose,
      onReject: () => setShowLeaveConfirmation(false),
      size: "auto"
    })]
  });
}
function ApproveAppDialogWithProviders(_ref) {
  let {
      onClose,
      onConfirm,
      initialUserIds
    } = _ref,
    variantProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(UnsavedChangesProvider, {
    children: /*#__PURE__*/_jsx(ApproveAppDialogInner, Object.assign({
      onClose: onClose,
      onConfirm: onConfirm,
      initialUserIds: initialUserIds
    }, variantProps))
  });
}
export function ApproveAppDialog(_ref2) {
  let {
      onClose,
      appName,
      appId,
      currentUserId,
      onConfirm,
      tracker,
      initialUserIds,
      isUngatedForTeamsSelection
    } = _ref2,
    variantProps = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  return /*#__PURE__*/_jsx(AppProvider, {
    appName: appName,
    appId: appId,
    currentUserId: currentUserId,
    children: /*#__PURE__*/_jsx(TrackingProvider, {
      tracker: tracker,
      children: /*#__PURE__*/_jsx(TeamsGateProvider, {
        isUngatedForTeamsGate: isUngatedForTeamsSelection,
        children: /*#__PURE__*/_jsx(ApproveAppDialogWithProviders, Object.assign({
          onClose: onClose,
          onConfirm: onConfirm,
          initialUserIds: initialUserIds
        }, variantProps))
      })
    })
  });
}
export default ApproveAppDialog;